import React, { useCallback, useState } from 'react';
import { Alert, Spin } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useMeQuery } from '../../queries/me';
import { useTeamPermissionsQuery } from '../../queries/users';
import { usePermissionMatrix } from './usePermissionMatrix';
import PermissionsMatrix from './PermissionsMatrix';
import RoleModal from './RoleModal';
import CompanyCard from './CompanyCard';
import BrandingCard from './BrandingCard';
import LocationsCard from './LocationsCard';
import AlertsCard from './AlertsCard';
import AssistantCard from './AssistantCard';
import PasswordCard from './PasswordCard';
import TeamCard from './TeamCard';
import KnowledgeCard from './KnowledgeCard';
import OnCallCard from './OnCallCard';
import BusinessHoursCard from './BusinessHoursCard';
import ProfileCard from './ProfileCard';
import { Columns, TabBody } from './styled';
import { FilterChips, PageHeader } from '../../components';

function PermissionsSection() {
  const { data } = useTeamPermissionsQuery();
  const { roles, sections } = usePermissionMatrix(data);
  const [selected, setSelected] = useState(null);
  const close = useCallback(() => setSelected(null), []);
  const section = sections.find((s) => s.group === selected);

  if (!sections.length) return null;
  return (
    <>
      <PermissionsMatrix roles={roles} sections={sections} onSelect={setSelected} />
      {section && <RoleModal section={section} roles={roles} onClose={close} />}
    </>
  );
}

const TABS = [
  { value: 'company', label: 'Company' },
  { value: 'team', label: 'Team & Permissions', perm: 'manage_users' },
  { value: 'assistant', label: 'Assistant', perm: 'manage_agents' },
  { value: 'knowledge', label: 'Knowledge & FAQ', perm: 'manage_knowledge' },
  { value: 'oncall', label: 'On-call', perm: 'manage_settings' },
  { value: 'account', label: 'Account' }
];

export default function Settings() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const { data: me, isPending, isError } = useMeQuery();
  const tabs = TABS.filter((t) => !t.perm || can(t.perm));
  const tab = tabs.find((t) => t.value === params.get('tab'))?.value || 'company';

  if (isPending) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: 240 }}>
        <Spin size="large" />
      </div>
    );
  }

  const org = me?.organization;

  return (
    <div>
      <PageHeader title="Settings" subtitle="Company profile, team access, assistant, knowledge, on-call and your account." />

      {isError && <Alert type="error" showIcon message="Failed to load settings." style={{ marginBottom: 16 }} />}

      <div style={{ marginBottom: 20 }}>
        <FilterChips
          label="Settings section"
          value={tab}
          onChange={(value) => setParams({ tab: value }, { replace: true })}
          options={tabs.map(({ value, label }) => ({ key: value, label }))}
        />
      </div>

      <TabBody key={tab}>
        {tab === 'company' && (
          <Columns>
            <div>
              <CompanyCard organization={org} offerings={me?.offerings} />
              <BrandingCard branding={org?.branding} />
            </div>
            <div>
              {can('manage_settings') && <BusinessHoursCard />}
              <LocationsCard />
            </div>
          </Columns>
        )}
        {tab === 'team' && (
          <>
            <TeamCard />
            <PermissionsSection />
          </>
        )}
        {tab === 'assistant' && (
          <Columns>
            <div>
              <AssistantCard />
            </div>
          </Columns>
        )}
        {tab === 'knowledge' && <KnowledgeCard />}
        {tab === 'oncall' && <OnCallCard />}
        {tab === 'account' && (
          <Columns>
            <div>
              <ProfileCard />
              <AlertsCard notifications={org?.notifications} />
            </div>
            <div>
              <PasswordCard />
            </div>
          </Columns>
        )}
      </TabBody>
    </div>
  );
}
